import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, User, LogOut, ChevronUp } from 'lucide-react';
import { cva } from 'class-variance-authority';
import PropTypes from 'prop-types';
import { useAuth } from '@/shared/context/AuthContext';

import { billingSecondarySidebar } from '@/feature/Billing/configs/sidebar.config.js';
import { rulesSecondarySidebar } from '@/feature/Rules/configs/sidebar.config.js';

const featureConfigs = {
  '/rules': rulesSecondarySidebar,
  '/subscription': billingSecondarySidebar,
  '/credit': billingSecondarySidebar,
  '/invoices': billingSecondarySidebar,
  '/payments': billingSecondarySidebar,
  '/billing': billingSecondarySidebar,
};

const matchesPathPattern = (pattern, pathname) => {
  const patternSegments = pattern.split('/').filter(Boolean);
  const pathSegments = pathname.split('/').filter(Boolean);

  return (
    patternSegments.length === pathSegments.length &&
    patternSegments.every(
      (segment, index) =>
        segment.startsWith(':') || segment === pathSegments[index],
    )
  );
};

const iconWrapperVariants = cva(
  'relative flex h-10 w-10 transform items-center justify-center rounded-xl transition-all duration-200',
  {
    variants: {
      active: {
        true: 'scale-105 shadow-md',
        false: 'scale-100 bg-transparent shadow-none active:scale-95',
      },
      isLogout: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        active: true,
        isLogout: false,
        className:
          'bg-primary-100 shadow-primary-20 dark:bg-blue-600 dark:shadow-blue-500/25',
      },
      {
        active: true,
        isLogout: true,
        className:
          'bg-gradient-to-br from-orange-500 to-orange-600 shadow-orange-500/20 dark:shadow-orange-500/25',
      },
    ],
    defaultVariants: {
      active: false,
      isLogout: false,
    },
  },
);

const iconVariants = cva('transition-colors duration-200', {
  variants: {
    active: {
      true: 'text-white',
      false: '',
    },
    isLogout: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      active: false,
      isLogout: false,
      className: 'text-tertiary-80 dark:text-gray-500',
    },
    {
      active: false,
      isLogout: true,
      className: 'text-orange-600 dark:text-orange-500',
    },
  ],
  defaultVariants: {
    active: false,
    isLogout: false,
  },
});

export default function MobileBottomNav({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [isSecondaryOpen, setIsSecondaryOpen] = useState(false);

  const secondarySidebarConfig = useMemo(() => {
    const matchedPath = Object.keys(featureConfigs).find((path) =>
      location.pathname.startsWith(path),
    );
    return matchedPath ? featureConfigs[matchedPath] : null;
  }, [location.pathname]);

  const hasSecondarySidebar = secondarySidebarConfig?.enabled || false;

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      onLogout();
    }
  };

  const handleSecondaryNavigation = (item) => {
    navigate(item.path);
    setIsSecondaryOpen(false);
  };

  const isSecondaryItemActive = (item) => {
    if (
      item.activePaths?.some((activePath) =>
        matchesPathPattern(activePath, location.pathname),
      )
    ) {
      return true;
    }

    const [itemPath, itemQuery = ''] = item.path.split('?');

    if (location.pathname !== itemPath) {
      return false;
    }

    const itemTab = new URLSearchParams(itemQuery).get('tab');
    const currentTab = new URLSearchParams(location.search).get('tab');

    if (itemTab) {
      return currentTab ? currentTab === itemTab : item.id === 'alert';
    }

    return true;
  };

  const toggleSecondaryNav = () => {
    setIsSecondaryOpen((prev) => !prev);
  };

  const navItems = [
    {
      icon: Home,
      path: '/',
      onClick: () => navigate('/'),
      ariaLabel: 'میز خدمت',
    },
    {
      icon: User,
      path: '/profile/info',
      matchPrefix: '/profile',
      onClick: () => navigate('/profile/info'),
      ariaLabel: 'پروفایل',
    },
    {
      icon: LogOut,
      onClick: handleLogout,
      isLogout: true,
      ariaLabel: 'خروج',
    },
  ];

  return (
    <>
      {hasSecondarySidebar && isSecondaryOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
          onClick={() => setIsSecondaryOpen(false)}
        />
      )}

      <div className="fixed right-0 bottom-0 left-0 z-50">
        {hasSecondarySidebar && (
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isSecondaryOpen ? 'max-h-72 opacity-100' : 'max-h-0 opacity-0'
            } border-tertiary-10 border-t bg-white dark:border-gray-800/60 dark:bg-gray-900`}
          >
            <div className="border-tertiary-10 border-b px-4 py-3 dark:border-gray-800/50">
              <h3 className="text-tertiary-90 text-sm font-bold dark:text-gray-200">
                {secondarySidebarConfig.title}
              </h3>
            </div>

            <div className="max-h-52 overflow-y-auto py-1">
              {secondarySidebarConfig.items.map((item) => {
                const Icon = item.icon;
                const active = isSecondaryItemActive(item);

                return (
                  <button
                    key={item.id}
                    onClick={() => handleSecondaryNavigation(item)}
                    className={`relative flex w-full items-center gap-3 px-4 py-3 transition-all duration-200 active:scale-[0.98] ${
                      active
                        ? 'bg-primary-100/30 text-primary-100 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'text-tertiary-80 active:bg-tertiary-6 dark:text-gray-400 dark:active:bg-gray-800/50'
                    }`}
                  >
                    {active && (
                      <div className="from-primary-100 to-primary-80 absolute top-1/2 right-0 h-8 w-1 -translate-y-1/2 rounded-l-full bg-gradient-to-b dark:from-blue-400 dark:to-blue-300" />
                    )}
                    <div
                      className={`flex-shrink-0 rounded-lg p-2 transition-all ${
                        active
                          ? 'bg-primary-100/10 text-primary-100 dark:bg-blue-500/20 dark:text-blue-400'
                          : 'bg-tertiary-6 text-tertiary-80 dark:bg-gray-800/50 dark:text-gray-400'
                      }`}
                    >
                      <Icon size={18} />
                    </div>
                    <div className="min-w-0 flex-1 text-right">
                      <p className="truncate text-sm font-semibold">
                        {item.label}
                      </p>
                      {item.description && (
                        <p
                          className={`mt-0.5 truncate text-xs ${
                            active ? 'opacity-80' : 'opacity-50'
                          }`}
                        >
                          {item.description}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <nav className="border-tertiary-10 border-t bg-white shadow-lg backdrop-blur-xl transition-all duration-300 dark:border-gray-800/50 dark:bg-gray-900 dark:shadow-2xl">
          <div className="flex items-center justify-around px-6">
            {hasSecondarySidebar && (
              <button
                onClick={toggleSecondaryNav}
                className="relative flex flex-1 items-center justify-center py-2 transition-all duration-200"
                aria-label="منوی بیشتر"
                aria-expanded={isSecondaryOpen}
              >
                <div
                  className={iconWrapperVariants({
                    active: isSecondaryOpen,
                    isLogout: false,
                  })}
                >
                  <ChevronUp
                    size={18}
                    strokeWidth={2.5}
                    className={`${iconVariants({
                      active: isSecondaryOpen,
                      isLogout: false,
                    })} transition-transform duration-300 ${
                      isSecondaryOpen ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </button>
            )}

            {navItems.map((item, index) => {
              const Icon = item.icon;
              let active = false;

              if (item.matchPrefix) {
                active = location.pathname.startsWith(item.matchPrefix);
              } else if (item.path) {
                active = location.pathname === item.path;
              }

              const isLogout = item.isLogout || false;

              return (
                <button
                  key={index}
                  onClick={item.onClick}
                  className="relative flex flex-1 items-center justify-center py-2 transition-all duration-200"
                  aria-label={item.ariaLabel}
                >
                  <div
                    className={iconWrapperVariants({
                      active,
                      isLogout,
                    })}
                  >
                    <Icon
                      size={18}
                      strokeWidth={2.5}
                      className={iconVariants({
                        active,
                        isLogout,
                      })}
                    />
                  </div>
                </button>
              );
            })}
          </div>

          <div
            style={{
              height: 'env(safe-area-inset-bottom, 0px)',
            }}
            className="w-full"
          />
        </nav>
      </div>
    </>
  );
}

MobileBottomNav.propTypes = {
  onLogout: PropTypes.func.isRequired,
};
